import { useEffect, useState, type CSSProperties } from 'react';
import { strings } from '@/shared/strings';
import styles from './Loading.module.css';

/**
 * The loading vocabulary. Section 11 and section 13.
 *
 * Everything that waits on data uses one of these four, and nothing renders a
 * loading sentence of its own. The reason is not tidiness: a screen reader has
 * to be told that the page is busy and then told when it is not, and that only
 * happens reliably when a single component owns the live region. The visible
 * skeleton is hidden from assistive technology, the label is what is announced.
 *
 * Pick by what is known about the wait:
 *
 *   Skeleton rows   a list whose row shape is already known
 *   Skeleton cards  a grid of tiles, same argument
 *   PageLoading     a whole route with nothing on screen yet
 *   Loading         a small region inside a screen that is otherwise drawn
 */

interface Busy {
  /** Announced, and shown next to the spinner. Section 13, no bare spinners. */
  label?: string;
}

/**
 * Holds a loading state back until the wait is long enough to be worth
 * reporting. A session or a cached query resolves in a few milliseconds and a
 * spinner that appears and vanishes in that window reads as a fault, which is
 * the argument the shell already made for rendering nothing at all. A delay
 * keeps that behaviour for the warm case and still says something on the cold
 * one, where a blank frame is what reads as a fault instead.
 */
function useAfter(delayMs: number): boolean {
  const [passed, setPassed] = useState(delayMs <= 0);

  useEffect(() => {
    if (delayMs <= 0) {
      setPassed(true);
      return;
    }
    setPassed(false);
    const id = setTimeout(() => setPassed(true), delayMs);
    return () => clearTimeout(id);
  }, [delayMs]);

  return passed;
}

/** The bare ring. Presentational, so it is hidden from the reader. */
export function Spinner({ size = 18 }: { size?: number }) {
  return <span className={styles.spinner} style={{ '--size': `${size}px` } as CSSProperties} />;
}

/** A wait inside a screen that has already drawn its heading and controls. */
export function Loading({ label = strings.common.loading }: Busy) {
  return (
    <div className={styles.inline} role="status" aria-live="polite">
      <Spinner size={16} />
      <span>{label}</span>
    </div>
  );
}

/** A wait with nothing else on the route to look at. */
export function PageLoading({
  label = strings.common.loading,
  delayMs = 0,
}: Busy & { delayMs?: number }) {
  const show = useAfter(delayMs);
  if (!show) return null;

  return (
    <div className={styles.page} role="status" aria-live="polite">
      <Spinner size={26} />
      <span>{label}</span>
    </div>
  );
}

/** One placeholder bar, for a value that is arriving into a fixed layout. */
export function SkeletonBar({ width, height }: { width: string; height?: number }) {
  const style = { '--w': width, ...(height === undefined ? {} : { '--h': `${height}px` }) };
  return <span className={styles.bar} style={style as CSSProperties} />;
}

/*
 * Widths that vary row to row. A column of identical grey bars reads as a
 * broken table; uneven ones read as text that has not arrived yet.
 */
const WIDTHS = ['62%', '48%', '71%', '55%', '66%', '43%'] as const;

/** Placeholder rows for a list or a table, in the shape the rows will take. */
export function SkeletonRows({ rows = 4, label = strings.common.loading }: Busy & { rows?: number }) {
  return (
    <div role="status" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      <div className={styles.rows} aria-hidden="true">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className={styles.row}>
            <div className={styles.rowMain}>
              <SkeletonBar width={WIDTHS[index % WIDTHS.length] ?? '60%'} height={13} />
              <SkeletonBar width="34%" height={10} />
            </div>
            <span className={styles.rowTail}>
              <SkeletonBar width="100%" height={22} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Placeholder tiles for a grid, for the same reason as the rows above. */
export function SkeletonCards({
  cards = 4,
  label = strings.common.loading,
}: Busy & { cards?: number }) {
  return (
    <div role="status" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      <div className={styles.cards} aria-hidden="true">
        {Array.from({ length: cards }, (_, index) => (
          <div key={index} className={styles.card}>
            <SkeletonBar width="46%" height={11} />
            <SkeletonBar width={WIDTHS[index % WIDTHS.length] ?? '60%'} height={20} />
            <SkeletonBar width="38%" height={10} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * A case on its way: the summary card and the details card in the places the
 * case will draw them, so opening a case does not show a spinner on an empty
 * page and nothing jumps when it arrives. Request of 5 October 2026, section 6.
 */
export function SkeletonCase({ label = strings.common.loading }: Busy) {
  return (
    <div role="status" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      <div aria-hidden="true">
        <div className={`${styles.card} ${styles.caseCard}`}>
          <SkeletonBar width="38%" height={26} />
          <div className={styles.caseFields}>
            {['54%', '62%', '48%', '58%'].map((width, index) => (
              <div key={index}>
                <SkeletonBar width="40%" height={9} />
                <SkeletonBar width={width} height={13} />
              </div>
            ))}
          </div>
        </div>
        <div className={`${styles.card} ${styles.caseCard}`}>
          <SkeletonBar width="30%" height={10} />
          {['70%', '52%', '64%', '44%'].map((width, index) => (
            <SkeletonBar key={index} width={width} height={14} />
          ))}
        </div>
      </div>
    </div>
  );
}
